'use client';

import { forwardRef, useImperativeHandle, useRef, type VideoHTMLAttributes } from 'react';
import { useVideoArming } from '@/lib/hooks/useVideoArming';

type NativeVideoProps = Omit<
  VideoHTMLAttributes<HTMLVideoElement>,
  'src' | 'preload' | 'muted' | 'playsInline' | 'loop' | 'autoPlay' | 'children'
>;

interface Props extends NativeVideoProps {
  src: string;
  initialLoad: boolean;
  initialPlay: boolean;
  armOnIdle?: boolean;
}

const DecorativeVideo = forwardRef<HTMLVideoElement, Props>(function DecorativeVideo(
  { src, initialLoad, initialPlay, armOnIdle, ...videoProps },
  ref,
) {
  const videoRef = useRef<HTMLVideoElement>(null);
  useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement, []);

  const { shouldLoadVideo } = useVideoArming({ videoRef, initialLoad, initialPlay, armOnIdle });

  return (
    <video
      {...videoProps}
      ref={videoRef}
      src={shouldLoadVideo ? src : undefined}
      preload={shouldLoadVideo ? 'metadata' : 'none'}
      muted
      playsInline
      loop
      autoPlay={initialPlay}
    >
      <track
        kind='captions'
        src='/captions/no-audio.vtt'
        srcLang='en'
        label='No spoken audio'
      />
    </video>
  );
});

export default DecorativeVideo;
